import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatRupiah } from "../lib/format";
import { tdClass, thClass } from "../components/Common";

const KAS_META = {
  resmi: { label: "Kas Resmi" },
  imf: { label: "Kas IMF" },
};

export default function Rekap() {
  const { hasPerm } = useAuth();
  const canResmi = hasPerm("view_saldo_resmi");
  const canImf = hasPerm("view_saldo_imf");
  const [book, setBook] = useState(canResmi ? "resmi" : "imf");
  const [data, setData] = useState(null);
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback((y, b) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (y) params.set("year", y);
    params.set("book", b);
    api.get(`/stats/monthly?${params.toString()}`)
      .then((r) => {
        setData(r.data);
        setYear(r.data.year);
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load("", book); }, [book, load]);

  const tabCls = (active) =>
    `px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${
      active ? "bg-slate-900 text-white shadow-sm" : "text-slate-400 hover:text-slate-100"
    }`;

  if (loading && !data)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="rekap-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-100">Rekap Bulanan {KAS_META[book].label} {year}</h2>
          <p className="text-xs text-slate-400">Saldo awal, pemasukan, pengeluaran, dan saldo akhir setiap bulan per kas</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-800 bg-slate-800 p-1" data-testid="rekap-kas-selector">
            {canResmi && (
              <button data-testid="rekap-tab-resmi" onClick={() => setBook("resmi")} className={tabCls(book === "resmi")}>
                Kas Resmi
              </button>
            )}
            {canImf && (
              <button data-testid="rekap-tab-imf" onClick={() => setBook("imf")} className={tabCls(book === "imf")}>
                Kas IMF
              </button>
            )}
          </div>
          <select
            data-testid="select-rekap-year"
            className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            value={year}
            onChange={(e) => load(e.target.value, book)}
          >
            {data?.years?.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="table-rekap-bulanan" className="w-full border-collapse text-sm text-left">
            <thead>
              <tr>
                <th className={thClass}>Bulan</th>
                <th className={`${thClass} text-right`}>Saldo Awal</th>
                <th className={`${thClass} text-right`}>Pemasukan</th>
                <th className={`${thClass} text-right`}>Pengeluaran</th>
                <th className={`${thClass} text-right`}>Saldo Akhir</th>
              </tr>
            </thead>
            <tbody>
              {data?.months?.map((m) => (
                <tr key={m.key} className={`hover:bg-slate-800/40 ${m.income === 0 && m.expense === 0 ? "text-slate-400" : ""}`}>
                  <td className={`${tdClass} font-semibold text-slate-100`}>{m.month}</td>
                  <td className={`${tdClass} text-right font-num`}>{formatRupiah(m.opening)}</td>
                  <td className={`${tdClass} text-right font-num text-emerald-400`}>{formatRupiah(m.income)}</td>
                  <td className={`${tdClass} text-right font-num text-rose-400`}>{formatRupiah(m.expense)}</td>
                  <td className={`${tdClass} text-right font-num font-semibold ${m.closing < 0 ? "text-rose-400" : "text-blue-300"}`}>{formatRupiah(m.closing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5">
          <h3 className="text-base font-bold text-slate-100">Pemasukan & Pengeluaran per Bulan</h3>
          <div data-testid="chart-rekap-income-expense" className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.months || []} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(m) => m.slice(0, 3)} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}jt`} />
                <Tooltip formatter={(v) => formatRupiah(v)} />
                <Legend />
                <Bar dataKey="income" name="Pemasukan" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Pengeluaran" fill="#F43F5E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5">
          <h3 className="text-base font-bold text-slate-100">Saldo per Bulan</h3>
          <div data-testid="chart-rekap-balance" className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.months || []} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(m) => m.slice(0, 3)} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}jt`} />
                <Tooltip formatter={(v) => formatRupiah(v)} />
                <Legend />
                <Line type="monotone" dataKey="closing" name="Saldo Akhir" stroke="#38BDF8" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
